import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
} from '@mui/material';
import { FirstPage, LastPage, ChevronLeft, ChevronRight, ViewList } from '@mui/icons-material';

const ProfessionalPagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
  itemName = 'items',
  color = 'primary',
  showItemsPerPage = true,
  itemsPerPageOptions = [5, 10, 15, 20],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalItems === 0) return null;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Left side - Items info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewList color={color} />
          <Typography variant="body2" color="text.secondary">
            Showing{' '}
            <strong>
              {startIndex}-{endIndex}
            </strong>{' '}
            of <strong>{totalItems}</strong> {itemName}
          </Typography>
        </Box>

        <Chip
          label={`Page ${currentPage} of ${totalPages}`}
          color={color}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Center - Page navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          size="small"
          color={color}
        >
          <FirstPage />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          size="small"
          color={color}
        >
          <ChevronLeft />
        </IconButton>

        {totalPages <= 7
          ? // Show all pages if 7 or fewer
            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <IconButton
                key={page}
                onClick={() => handlePageChange(page)}
                size="small"
                color={currentPage === page ? color : 'default'}
                sx={{
                  minWidth: 32,
                  bgcolor: currentPage === page ? `${color}.light` : 'transparent',
                  '&:hover': {
                    bgcolor: currentPage === page ? `${color}.light` : 'action.hover',
                  },
                }}
              >
                <Typography variant="body2" fontWeight={currentPage === page ? 'bold' : 'normal'}>
                  {page}
                </Typography>
              </IconButton>
            ))
          : // Show pages with ellipsis
            getVisiblePages().map((page, index) =>
              page === '...' ? (
                <Typography key={index} variant="body2" sx={{ px: 1 }}>
                  ...
                </Typography>
              ) : (
                <IconButton
                  key={page}
                  onClick={() => handlePageChange(page)}
                  size="small"
                  color={currentPage === page ? color : 'default'}
                  sx={{
                    minWidth: 32,
                    bgcolor: currentPage === page ? `${color}.light` : 'transparent',
                    '&:hover': {
                      bgcolor: currentPage === page ? `${color}.light` : 'action.hover',
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={currentPage === page ? 'bold' : 'normal'}>
                    {page}
                  </Typography>
                </IconButton>
              )
            )}

        <IconButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          size="small"
          color={color}
        >
          <ChevronRight />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          size="small"
          color={color}
        >
          <LastPage />
        </IconButton>
      </Box>

      {/* Right side - Items per page */}
      {showItemsPerPage && onItemsPerPageChange && (
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Per page</InputLabel>
          <Select
            value={itemsPerPage}
            label="Per page"
            onChange={e => onItemsPerPageChange(e.target.value)}
          >
            {itemsPerPageOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option} {itemName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Paper>
  );
};

export default ProfessionalPagination;
